import { useBoolean } from 'ahooks';
import Input from 'antd/es/input';
import isEqual from 'lodash/isEqual';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button as ButtonAtom } from 'components/atoms/button';
import { formatter, InputDuration } from 'components/atoms/input-duration';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { PLACEHOLDER } from 'components/organisms/task-pane/task-detail/task-fields/task-custom-fields/fields/constant';
import { NumberField } from 'components/organisms/tasks/fields';
import { OutlineError } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { queueMacrotask } from 'utils/macrotask';

const Button = styled(ButtonAtom)`
  width: 100%;
`;
const InputWrapper = styled(OutsideClickHandler)`
  width: 100%;
`;

const DurationInput = styled(InputDuration)`
  width: 100%;

  &.has-error {
    ${OutlineError};

    &:focus {
      box-shadow: none;
      ${OutlineError};
    }
  }
`;

interface EffortFieldProps {
  value?: number;
  field: NumberField;
  onChange: (latest: number | null) => void;
}

export const EffortDurationField: FC<EffortFieldProps> = ({
  value,
  field,
  onChange,
}) => {
  const [isReadOnly, { setFalse, setTrue }] = useBoolean(true);
  const [effortValue, setEffortValue] = useState<number | undefined>();
  const effortInputPlaceholder = useTranslation(
    'timesheets.placeholders.time_tracked',
  );

  useEffect(() => {
    setEffortValue(value);
  }, [value]);

  const handleChange = useCallback(
    (latest: number) => {
      setEffortValue(latest);
      const latestEffort = Number(latest);

      if (!isEqual(latestEffort, value ?? 0)) {
        onChange(latestEffort === 0 ? null : latestEffort);
      }
    },
    [value, onChange, setEffortValue],
  );

  const ref = useRef<Input>(null);

  const onClick = () => {
    setFalse();
    queueMacrotask(() => ref.current?.focus({ cursor: 'all' }));
  };
  const defaultValue = field.metaData?.default as number;
  const renderValue = (v) => `${formatter(v)}`;

  return isReadOnly ? (
    <Button type="text" onClick={onClick}>
      {effortValue != null && effortValue !== 0
        ? renderValue(effortValue)
        : defaultValue
        ? renderValue(defaultValue)
        : PLACEHOLDER}
    </Button>
  ) : (
    <InputWrapper onOutsideClick={setTrue}>
      <DurationInput
        ref={ref}
        placeholder={effortInputPlaceholder}
        min={field.metaData?.range?.min ?? 0}
        max={field.metaData?.range?.max}
        defaultValue={defaultValue}
        value={effortValue}
        onChange={handleChange}
        onPressEnter={({ currentTarget }) => currentTarget.blur()}
        onBlur={() => queueMacrotask(setTrue)}
      />
    </InputWrapper>
  );
};
